import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

export const api = axios.create({ baseURL: API, timeout: 120000 });

api.interceptors.request.use((config) => {
    const token = localStorage.getItem("fs_token");
    if (token) config.headers.Authorization = `Bearer ${token}`;
    // Tell backend AI prompts which language to produce content in.
    // LocaleContext persists this key; default to English if unset.
    try {
        const loc = localStorage.getItem("fs_locale") || "en";
        config.headers["X-FirmScope-Locale"] = loc;
    } catch { /* private mode — leave header off, backend will default to 'en' */ }
    return config;
});

export const TOKEN_KEY = "fs_token";
export const USER_KEY = "fs_user";

/**
 * Poll an AI job until done/error. Bypasses the 30s ingress timeout.
 * @param {string} jobId
 * @param {object} opts
 *   - intervalMs: poll interval (default 2000)
 *   - timeoutMs: total timeout (default 5 minutes)
 *   - signal: AbortSignal to cancel
 * @returns {Promise<any>} the job.result
 */
export async function pollJob(jobId, opts = {}) {
    const interval = opts.intervalMs ?? 2000;
    const timeout = opts.timeoutMs ?? 5 * 60 * 1000;
    const started = Date.now();
    // small initial delay so the background task has a chance to flip to 'running'
    await new Promise((r) => setTimeout(r, 600));
    while (true) {
        if (opts.signal?.aborted) throw new Error("Cancelled");
        if (Date.now() - started > timeout) throw new Error("Job timed out");
        const { data } = await api.get(`/jobs/${jobId}`);
        if (data.status === "done") return data.result;
        if (data.status === "error") {
            const e = new Error(data.error || "AI job failed");
            e.jobError = true;
            throw e;
        }
        await new Promise((r) => setTimeout(r, interval));
    }
}

/** Friendly toast text for a 429 axios error. */
export function rateLimitMessage(err) {
    if (err?.response?.status !== 429) return null;
    const d = err.response.data?.detail;
    return typeof d === "string" ? d : "Rate limit reached. Please slow down.";
}
